import React, { useState, useCallback, useRef, useEffect } from 'react';

interface ViewShellProps {
  /** Content to render in the sidebar between header and user menu */
  sidebarContent: React.ReactNode;
  /** Main content to render */
  children: React.ReactNode;
  /** Optional custom className for main content area */
  mainClassName?: string;
  /** Hide the sidebar completely (for full-width wizard views) */
  hideSidebar?: boolean;
}

const MIN_SIDEBAR_WIDTH = 200;
const MAX_SIDEBAR_WIDTH = 400;
const DEFAULT_SIDEBAR_WIDTH = 256; // w-64 = 16rem = 256px

/**
 * Base shell component providing consistent layout structure for both
 * Categories and Accounts views. Renders inside the V2 frame, which owns the
 * nav rail and viewport height. The resize drag is delta-based so the frame's
 * left offset does not enter the math.
 *
 * Layout: resizable sidebar + flexible main area
 */
export function ViewShell({
  sidebarContent,
  children,
  mainClassName = 'flex-1 min-h-0 overflow-hidden flex flex-col',
  hideSidebar = false,
}: ViewShellProps) {
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = sidebarWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [sidebarWidth]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      const delta = e.clientX - startX.current;
      const newWidth = Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, startWidth.current + delta));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <div className="h-full bg-bg-secondary overflow-hidden">
      <div className="flex h-full overflow-hidden">
        {/* Context sidebar - hidden in full-width modes like wizard */}
        {!hideSidebar && (
          <aside
            className="flex-none bg-bg-raised border-r border-border-secondary flex flex-col relative"
            style={{ width: sidebarWidth }}
          >
            {/* Dynamic sidebar content */}
            {sidebarContent}

            {/* Resize handle */}
            <div
              onMouseDown={handleMouseDown}
              className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blueberry-400 active:bg-blueberry-500 transition-colors z-10"
            />
          </aside>
        )}

        {/* Main content area */}
        <main className={mainClassName}>
          {children}
        </main>
      </div>
    </div>
  );
}
